import { useEffect, useState } from "react";
import { api, money, cabDateTime, day } from "../api.js";
import { SectionHead, PartialErrors, Empty, shortName } from "./common.jsx";

// СЕА ПДВ: рахунок і рух коштів (vattrans) + реєстр операцій з лімітом реєстрації (rstoper)
export default function Sea({ companyId, res, version, onSync }) {
  const [data, setData] = useState(null);
  const [tab, setTab] = useState("ops");
  useEffect(() => { api(`/api/companies/${companyId}/sea`).then(setData); }, [companyId, version]);
  const ops = data?.operations || [];
  const trans = [...(data?.transactions || [])].sort((a, b) => String(b.pmtDate).localeCompare(String(a.pmtDate)));
  const last = trans[0];
  const miss = data?.notInSea || [];
  const showMiss = () => { setTab("miss"); requestAnimationFrame(() => document.getElementById("sea-tabs")?.scrollIntoView({ behavior: "smooth", block: "start" })); };
  return (
    <>
      <SectionHead title="СЕА ПДВ" res={res} onSync={onSync} />
      <PartialErrors res={res} />
      {!data ? <p className="muted">Завантаження…</p> : !ops.length && !trans.length ? <Empty>Даних СЕА ще немає. Натисніть «Оновити СЕА» вгорі.</Empty> : (
        <>
          <div className="card stats">
            <div><span className="muted">Ліміт реєстрації</span><b>{ops.length ? money(ops[0].sumlm) : "—"}</b></div>
            <div><span className="muted">Залишок на рахунку</span><b>{last ? money(last.balance) : "—"}</b></div>
            <div><span className="muted">Рахунок СЕА</span><b className="mono">{last?.pdvAccount || "—"}</b></div>
            <div><span className="muted">ПН/РК без операції в реєстрі СЕА</span>{data.invoicesNotInSea ? <button type="button" className="stat-link" onClick={showMiss} aria-label={`Показати ${data.invoicesNotInSea} ПН/РК без операції в реєстрі СЕА`}>{data.invoicesNotInSea}</button> : <b>{data.invoicesNotInSea ?? "—"}</b>}</div>
          </div>
          <div className="card">
            <div className="tabs" role="group" aria-label="Дані СЕА" id="sea-tabs">
              <button type="button" className={tab === "ops" ? "tab active" : "tab"} aria-pressed={tab === "ops"} onClick={() => setTab("ops")}>Операції ({ops.length})</button>
              <button type="button" className={tab === "trans" ? "tab active" : "tab"} aria-pressed={tab === "trans"} onClick={() => setTab("trans")}>Рух коштів ({trans.length})</button>
              {miss.length > 0 && <button type="button" className={tab === "miss" ? "tab active" : "tab"} aria-pressed={tab === "miss"} onClick={() => setTab("miss")}>Без операції в СЕА ({miss.length})</button>}
            </div>
            {tab === "miss" ? <NotInSea companyId={companyId} rows={miss} /> : tab === "ops" ? (
              <div className="table-wrap">
                <table className="table-static">
                  <thead><tr><th>Дата</th><th>Операція</th><th>Документ</th><th>Контрагент</th><th className="num">Сума</th><th className="num">Ліміт після</th><th>ЄРПН</th></tr></thead>
                  <tbody>
                    {ops.map((o) => (
                      <tr key={o.rid}>
                        <td>{cabDateTime(o.opdt)}</td>
                        <td className="wrap-cell">{o.optype_name}</td>
                        <td>{o.docnum}{o.docdt ? <span className="muted"> від {day(o.docdt)}</span> : null}</td>
                        <td className="wrap-cell">{o.cpfullname}{o.cptin ? <><br /><span className="muted">{o.cptin}</span></> : null}</td>
                        <td className="num">{money(o.opsum)}</td>
                        <td className="num">{money(o.sumlm)}</td>
                        <td>{o.nlnkcd ? (o.has_invoice ? "є" : <span className="muted">немає</span>) : ""}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="table-wrap">
                <table className="table-static">
                  <thead><tr><th>Дата</th><th>Транзакція</th><th className="num">Сума</th><th className="num">Залишок</th><th className="num">Борг</th></tr></thead>
                  <tbody>
                    {trans.map((t, i) => (
                      <tr key={t.id ?? i}>
                        <td>{cabDateTime(t.pmtDate)}</td>
                        <td className="wrap-cell">{t.pmtFlagName}{t.pmtNazn ? <><br /><span className="muted">{t.pmtNazn}</span></> : null}</td>
                        <td className="num">{money(t.pmtSum)}</td>
                        <td className="num">{money(t.balance)}</td>
                        <td className="num">{money(t.debt)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}
    </>
  );
}

// ПН/РК, що є в ЄРПН, але без операції в реєстрі СЕА — з поясненням, чому так буває
const why = (i) => (Number(i.status) === 1 ? ["зупинено — операції не буде, доки ПН не зареєструють", "idle"]
  : Number(i.vat_sum) === 0 ? ["ПДВ 0 — ліміт не змінює, операції й не має бути", "idle"]
  : ["зареєстровано, а операції в СЕА немає — оновіть СЕА; якщо лишиться, звірте з кабінетом", "warn"]);

function NotInSea({ companyId, rows }) {
  const bad = rows.filter((i) => why(i)[1] === "warn").length;
  return (
    <>
      <p className="muted small">Кожна зареєстрована ПН/РК з ПДВ має операцію в СЕА, що змінює ліміт реєстрації.{bad ? ` Потребують уваги: ${bad}.` : " Усі випадки пояснюються статусом або нульовим ПДВ."}</p>
      <div className="table-wrap">
        <table className="table-static">
          <thead><tr><th>Зареєстровано</th><th>Документ</th><th>Контрагент</th><th className="num">ПДВ</th><th>Чому немає в СЕА</th></tr></thead>
          <tbody>
            {rows.map((i) => {
              const [text, tone] = why(i);
              return (
                <tr key={i.code}>
                  <td>{day(i.impdate)}</td>
                  <td><a href={`#/c/${companyId}/erpn/${i.direction}/${i.code}`}>{i.direction === "in" ? "Отримана" : "Видана"} {i.doc_kind || "ПН"} № {i.nmr}</a><br /><span className="muted small">від {day(i.crtdate)}</span></td>
                  <td className="wrap-cell">{shortName(i.direction === "in" ? i.seller : i.buyer)}</td>
                  <td className="num">{money(i.vat_sum)}</td>
                  <td className="wrap-cell"><span className={tone === "warn" ? "miss-warn small" : "muted small"}>{text}</span></td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </>
  );
}
